import {
  Trophy,
  Sparkles,
  Zap,
  Target,
  Lightbulb,
  Rocket,
  Crown,
  Medal,
  Coins,
  Smartphone,
  Tablet,
  Monitor,
  Gamepad2
} from 'lucide-react'

interface LandingHeroProps {
  onSignIn: () => void
  onSignUp: () => void
}

const trustItems = [
  { icon: Smartphone, label: 'Celular' },
  { icon: Tablet, label: 'Tablet' },
  { icon: Monitor, label: 'Computador' },
  { icon: Gamepad2, label: 'Gamificação completa' },
  { icon: Sparkles, label: 'Competências para o futuro' }
]

export function LandingHero({ onSignIn, onSignUp }: LandingHeroProps) {
  return (
    <section
      id="topo"
      className="relative overflow-hidden bg-brand-dark text-white pt-28 pb-20 md:pt-36 md:pb-32"
    >
      {/* glow decorativo */}
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -top-24 -left-24 size-96 rounded-full bg-brand-purple blur-3xl opacity-50" />
        <div className="absolute top-1/3 -right-32 size-[28rem] rounded-full bg-brand-blue blur-3xl opacity-60" />
        <div className="absolute bottom-0 left-1/3 size-80 rounded-full bg-brand-green blur-3xl opacity-20" />
      </div>

      {/* grid overlay sutil */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}
      />

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 md:px-8 lg:grid-cols-2 lg:items-center lg:gap-14">
        {/* Texto — vem primeiro no DOM e visualmente em todas as telas: info + CTAs têm prioridade sobre a ilustração */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-brand-green backdrop-blur">
            <Sparkles className="size-4" /> Nova temporada disponível
          </div>

          <h1 className="mt-6 font-display text-4xl font-black leading-[0.95] sm:text-5xl md:text-7xl lg:text-[5.5rem]">
            Transforme seu potencial em{' '}
            <span className="font-hl text-brand-yellow">ação</span>
          </h1>

          <p className="mt-4 font-hl text-xl text-brand-green sm:text-2xl md:text-3xl">
            Sua próxima aventura começa aqui.
          </p>

          <p className="mt-6 max-w-xl text-base text-white/85 md:text-lg lg:text-xl">
            Aprenda através de histórias interativas em formato Webtoon,
            desafios e missões que desenvolvem competências para a vida, para
            a escola e para o seu futuro.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button
              onClick={onSignUp}
              className="group relative inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-7 py-4 text-base font-extrabold text-primary-foreground shadow-[0_6px_0_var(--color-primary-dark)] transition hover:-translate-y-0.5 hover:shadow-[0_8px_0_var(--color-primary-dark)] cursor-pointer"
            >
              <Rocket className="size-5" />
              Criar Conta Grátis
            </button>
            <button
              onClick={onSignIn}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-white/25 bg-white/5 px-7 py-4 text-base font-extrabold text-white backdrop-blur transition hover:bg-white/15 cursor-pointer"
            >
              Já tenho conta
            </button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/70">
            {trustItems.map((t) => (
              <div key={t.label} className="flex items-center gap-2">
                <t.icon className="size-4 text-brand-green" /> {t.label}
              </div>
            ))}
          </div>
        </div>

        {/* Ilustração — secundária: menor e abaixo do conteúdo em mobile, sem competir com CTAs */}
        <div className="relative mt-6 lg:mt-0">
          <HeroIllustration />
        </div>
      </div>
    </section>
  )
}

function HeroIllustration() {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[19rem] sm:max-w-sm md:max-w-md lg:max-w-xl">
      {/* Card de nível central */}
      <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 rounded-[2rem] bg-gradient-to-br from-brand-purple to-brand-blue p-4 shadow-2xl ring-1 ring-white/20 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid size-11 shrink-0 place-items-center rounded-2xl bg-brand-yellow font-hl text-xl text-brand-dark shadow-[0_4px_0_var(--color-brand-dark)] sm:size-14 sm:text-2xl">
              12
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/70 sm:text-xs">
                Nível atual
              </div>
              <div className="font-hl text-lg text-white sm:text-2xl">Protagonista</div>
            </div>
          </div>
          <Crown className="size-6 text-brand-yellow sm:size-8" />
        </div>

        <div className="mt-6">
          <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-white/80">
            <span>XP</span>
            <span>2.450 / 3.000</span>
          </div>
          <div className="mt-2 h-4 w-full overflow-hidden rounded-full bg-white/15">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-green to-brand-yellow shadow-[0_0_18px_var(--color-brand-green)]"
              style={{ width: '82%' }}
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {[
            { label: 'Criatividade', c: 'bg-brand-pink text-white' },
            { label: 'Comunicação', c: 'bg-brand-cyan text-brand-dark' },
            { label: 'Inteligência Emocional', c: 'bg-brand-magenta text-white' },
            { label: 'Proatividade', c: 'bg-brand-coral text-brand-dark' },
            { label: 'Planejamento', c: 'bg-brand-green text-brand-dark' }
          ].map((t) => (
            <span
              key={t.label}
              className={`rounded-full px-3 py-1 text-xs font-bold ${t.c}`}
            >
              {t.label}
            </span>
          ))}
        </div>
      </div>

      {/* Chips flutuantes — reduzidos em mobile para não competir com o conteúdo */}
      <FloatChip
        className="left-0 top-4 -rotate-6"
        color="bg-brand-green text-brand-dark"
        icon={<Zap className="size-3.5 sm:size-4" />}
        title="XP +250"
        sub="Missão Concluída"
        delay="0s"
      />
      <FloatChip
        className="right-0 top-0 rotate-[8deg]"
        color="bg-brand-yellow text-brand-dark"
        icon={<Medal className="size-3.5 sm:size-4" />}
        title="Nova Medalha"
        sub="Jovem Descolado"
        delay="1.2s"
      />
      <FloatChip
        className="-left-2 bottom-16 -rotate-[4deg]"
        color="bg-brand-pink text-white"
        icon={<Target className="size-3.5 sm:size-4" />}
        title="Missão Completa"
        sub="Projeto de Vida"
        delay="2.1s"
      />
      <FloatChip
        className="-right-2 bottom-6 rotate-[6deg]"
        color="bg-brand-coral text-white"
        icon={<Trophy className="size-3.5 sm:size-4" />}
        title="Top 10 Ranking"
        sub="Sua Escola"
        delay="0.6s"
      />
      <FloatChip
        hideOnMobile
        className="right-10 -bottom-2 -rotate-[3deg]"
        color="bg-brand-cyan text-brand-dark"
        icon={<Coins className="size-4" />}
        title="+120 Moedas"
        sub="Resgate Disponível"
        delay="1.8s"
      />
      <FloatChip
        hideOnMobile
        className="left-8 -top-2 rotate-[4deg]"
        color="bg-white text-brand-dark"
        icon={<Lightbulb className="size-4 text-brand-purple" />}
        title="Nova Ideia"
        sub="Compartilhada"
        delay="0.9s"
      />
    </div>
  )
}

interface FloatChipProps {
  className?: string
  color: string
  icon: React.ReactNode
  title: string
  sub: string
  delay?: string
  hideOnMobile?: boolean
}

function FloatChip({
  className,
  color,
  icon,
  title,
  sub,
  delay,
  hideOnMobile
}: FloatChipProps) {
  return (
    <div
      className={`absolute ${hideOnMobile ? 'hidden sm:block' : ''} ${className} animate-float`}
      style={{ animationDelay: delay }}
    >
      <div
        className={`flex items-center gap-2 rounded-xl ${color} px-2 py-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.35)] ring-1 ring-black/10 sm:gap-3 sm:rounded-2xl sm:px-3 sm:py-2.5`}
      >
        <div className="grid size-6 shrink-0 place-items-center rounded-lg bg-black/10 sm:size-8">
          {icon}
        </div>
        <div className="pr-1">
          <div className="font-hl text-xs leading-none sm:text-sm">{title}</div>
          <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider opacity-80 sm:text-[10px]">
            {sub}
          </div>
        </div>
      </div>
    </div>
  )
}
